import {
  type BaseQueryFn,
  createApi,
  type FetchArgs,
  fetchBaseQuery,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'

import { isReauthRequiredError } from '~/shell/auth/reauthRequiredError'
import { getApiBaseUrl, shouldBypassAuthForE2E } from '~/shell/config'

import { customerHolder } from './customer'
import { sec } from './security'

/**
 * Serialise query params so array values become repeated keys (`?status=Active&status=Discharged`) rather than the
 * comma-joined single value (`?status=Active,Discharged`) that `fetchBaseQuery`'s default `URLSearchParams` pass would
 * produce. FastAPI list-typed query params expect repeated keys, so this is what multi-value filters require on the
 * wire. `null`/`undefined` values are dropped, matching the default's `stripUndefined` behaviour.
 */
const serializeParams = (params: Record<string, unknown>): string => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) {
      continue
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null) {
          search.append(key, String(item))
        }
      }
    } else {
      search.append(key, String(value))
    }
  }
  return search.toString()
}

/**
 * Dynamic base query that resolves the API base URL per-request.
 * This ensures the runtime config is loaded before the base URL is accessed.
 */
const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const rawBaseQuery = fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    paramsSerializer: serializeParams,
    prepareHeaders: async (headers) => {
      // Under the env-gated E2E bypass there is no Auth0 session, so skip the
      // token fetch entirely; the Playwright smoke mocks the API and does not
      // assert on the bearer header. Inert in normal dev and production.
      if (shouldBypassAuthForE2E) {
        return headers
      }

      try {
        const token = await sec.getAccessTokenSilently()
        if (token) {
          headers.set('authorization', `Bearer ${token}`)
        }
      } catch (error) {
        // A re-auth-required error means the cached session is expired or rotated-out:
        // - kick off interactive login rather than firing a doomed header-less request
        //   that would only 401 and strand the app on its loader.
        // Any other failure (transient network/timeout) is left to fall through.
        if (isReauthRequiredError(error)) {
          sec.requestReauth()
        }
      }
      return headers
    },
    credentials: 'include',
  })

  // Centrally inject the active customer as a query param so call sites never have to repeat it. The base query runs
  // outside React and cannot read route params, so it reads the customer from the holder bridge instead. Append-if-set:
  // when no customer is active (pre-auth or admin-scoped requests) the param is simply omitted, no endpoint special-casing.
  //
  // The caller's params are spread last, so an explicit `customer_regard_id` read straight from the route wins over the
  // holder rather than being clobbered by it.
  const customerRegardId = customerHolder.get()
  if (customerRegardId === null) {
    return rawBaseQuery(args, api, extraOptions)
  }

  // `args` may be a bare URL string or a FetchArgs object — normalize to object form before merging params so we
  // preserve any params the caller already supplied rather than clobbering them.
  const fetchArgs: FetchArgs = typeof args === 'string' ? { url: args } : args
  return rawBaseQuery(
    {
      ...fetchArgs,
      params: {
        customer_regard_id: customerRegardId,
        ...fetchArgs.params,
      },
    },
    api,
    extraOptions,
  )
}

export const baseApi = createApi({
  baseQuery: dynamicBaseQuery,
  endpoints: () => ({}),
  reducerPath: 'api',
})
